import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Rhino3dmLoader } from "three/examples/jsm/loaders/3DMLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { Boxes, Gem, Maximize2, Minimize2, Sun, Ruler, X } from "lucide-react";
import api, { API } from "@/lib/api";

const RHINO_LIB = "https://cdn.jsdelivr.net/npm/rhino3dm@8.17.0/";

const LIGHTING = {
  studio: { ambient: 0.9, key: 2.2, fill: 1.1, exposure: 1.25, label: "Studio" },
  soft: { ambient: 1.5, key: 1.2, fill: 0.8, exposure: 1.15, label: "Soft" },
  contrast: { ambient: 0.4, key: 3.4, fill: 1.6, exposure: 1.45, label: "Contrast" },
};

const MATERIALS = {
  platinum: { color: 0xe5e7eb, metalness: 1, roughness: 0.35, label: "Platinum" },
  gold: { color: 0xd4af37, metalness: 1, roughness: 0.3, label: "Gold" },
  rose: { color: 0xdca9a0, metalness: 1, roughness: 0.35, label: "Rose Gold" },
  silver: { color: 0xc0c5cc, metalness: 1, roughness: 0.25, label: "Silver" },
};

const forEachMesh = (obj, fn) => {
  if (!obj) return;
  if (obj.isMesh) fn(obj);
  else obj.traverse((c) => c.isMesh && fn(c));
};

/**
 * Strip the "leak-detection marker triangle" the server inserts at
 * [9999.9, 9999.9, 9999.9] inside every preview STL. It's meant to be
 * invisible, but three.js's Box3 includes it in the bounding box, which
 * blows up the fit-scale and turns the real jewellery into an invisible
 * speck. We drop every triangle that references a vertex beyond ~5000
 * world-units, since no legitimate jewellery mesh lives out there.
 */
const stripFarTriangles = (geometry, threshold = 5000) => {
  const pos = geometry.getAttribute("position");
  if (!pos) return;
  const src = pos.array;
  const triCount = src.length / 9; // 3 verts × 3 coords per triangle
  const kept = new Float32Array(src.length);
  let w = 0;
  for (let t = 0; t < triCount; t++) {
    const o = t * 9;
    let far = false;
    for (let k = 0; k < 9 && !far; k++) {
      if (Math.abs(src[o + k]) > threshold) far = true;
    }
    if (!far) {
      for (let k = 0; k < 9; k++) kept[w++] = src[o + k];
    }
  }
  if (w === src.length) return; // nothing stripped
  const trimmed = kept.subarray(0, w);
  geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(new Float32Array(trimmed), 3),
  );
  const normal = geometry.getAttribute("normal");
  if (normal) geometry.deleteAttribute("normal");
  geometry.setIndex(null);
};

export default function CadViewer({ listingId, url, editable, dmUrl }) {
  const mountRef = useRef(null);
  const meshRef = useRef(null);
  const containerRef = useRef(null);
  const lightsRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const displayToMmRef = useRef(1);                 // multiply display-units by this to get mm
  const measureLinesRef = useRef([]);               // THREE.Line objects added to the scene
  const [wireframe, setWireframe] = useState(false);
  const [mat, setMat] = useState("platinum");
  const [lighting, setLighting] = useState("studio");
  const [fullscreen, setFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0); // 0..1, or -1 for indeterminate
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState(false);
  const [source, setSource] = useState("stl"); // "stl" | "3dm"
  const [measureMode, setMeasureMode] = useState(false);
  const [pendingPoint, setPendingPoint] = useState(null); // world-space THREE.Vector3
  const [measurements, setMeasurements] = useState([]);   // [{a: Vec3, b: Vec3, mm: number}]
  const [showTip, setShowTip] = useState(false);          // first-time tooltip

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const width = mount.clientWidth;
    const height = mount.clientHeight;
    setLoading(true);
    setError(false);
    setProgress(0);
    setSlow(false);
    const slowTimer = setTimeout(() => setSlow(true), 8000);

    const onProgress = (xhr) => {
      if (xhr && xhr.total) {
        setProgress(Math.min(1, xhr.loaded / xhr.total));
      } else if (xhr && xhr.loaded) {
        // Content-Length missing — flip to indeterminate mode
        setProgress(-1);
      }
    };

    const scene = new THREE.Scene();
    scene.background = null;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(3, 2, 4);

    // `preserveDrawingBuffer: true` — required so that PhotoManager can call
    // `canvas.toDataURL()` to capture a still frame at the current camera
    // angle. Costs a small perf hit (~5-10%) but is necessary for the
    // "Add photo from 3D viewer" feature. Safe to keep on always.
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = LIGHTING[lighting].exposure;
    mount.appendChild(renderer.domElement);

    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

    const ambient = new THREE.AmbientLight(0xffffff, 0.9);
    scene.add(ambient);
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(5, 8, 6);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0x7dd3fc, 1.1);
    fill.position.set(-6, -2, -4);
    scene.add(fill);
    lightsRef.current = { ambient, key, fill, renderer };

    const grid = new THREE.GridHelper(20, 40, 0x475d80, 0x24324a);
    grid.position.y = -1.4;
    scene.add(grid);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 1.6;

    const mkMaterial = () =>
      new THREE.MeshStandardMaterial({
        color: MATERIALS.platinum.color,
        metalness: 1,
        roughness: 0.3,
        emissive: 0x1e40af,
        emissiveIntensity: 0,
        wireframe: false,
        side: THREE.DoubleSide,
      });

    const placeObject = (obj) => {
      forEachMesh(obj, (m) => (m.material = mkMaterial()));
      const box = new THREE.Box3().setFromObject(obj);
      const sphere = box.getBoundingSphere(new THREE.Sphere());
      const s = 1.9 / (sphere.radius || 1);
      obj.scale.setScalar(s);
      obj.position.set(-sphere.center.x * s, -sphere.center.y * s, -sphere.center.z * s);
      scene.add(obj);
      meshRef.current = obj;
      // Store scene refs + display→mm factor so the measurement tool can
      // convert click-picked points back to real millimetres.
      sceneRef.current = scene;
      cameraRef.current = camera;
      controlsRef.current = controls;
      // STL industry standard for jewellery is millimetres. Object was
      // scaled by `s` for viewing, so `1 / s` converts a display-unit
      // distance back to the mesh's original mm units.
      displayToMmRef.current = 1 / s;
      applyMaterial();
      setLoading(false);
    };

    const applyMaterial = () => {
      const conf = MATERIALS[mat];
      forEachMesh(meshRef.current, (m) => {
        m.material.wireframe = wireframe;
        m.material.color.setHex(wireframe ? 0x9fd8ff : conf.color);
        m.material.metalness = wireframe ? 0.9 : conf.metalness;
        m.material.roughness = conf.roughness;
        if (m.material.emissive) m.material.emissive.setHex(0x1e40af);
        m.material.emissiveIntensity = wireframe ? 0.5 : 0.0;
        m.material.needsUpdate = true;
      });
    };

    if (source === "3dm" && dmUrl) {
      const loader = new Rhino3dmLoader();
      loader.setLibraryPath(RHINO_LIB);
      loader.load(
        dmUrl,
        (obj) => {
          let has = false;
          forEachMesh(obj, () => (has = true));
          if (!has) return setError(true) || setLoading(false);
          placeObject(obj);
        },
        onProgress,
        () => {
          setError(true);
          setLoading(false);
        }
      );
    } else if (typeof url === "string" && url.toLowerCase().includes(".glb")) {
      // Compact GLB preview (decimated + repacked) — loads much faster than
      // the raw STL. Server backfills on-demand for older listings.
      const loader = new GLTFLoader();
      loader.load(
        url,
        (gltf) => {
          let geom = null;
          gltf.scene.traverse((o) => {
            if (!geom && o.isMesh && o.geometry) geom = o.geometry;
          });
          if (!geom) {
            setError(true);
            setLoading(false);
            return;
          }
          if (!geom.attributes.normal) geom.computeVertexNormals();
          geom.center();
          const mesh = new THREE.Mesh(geom, mkMaterial());
          placeObject(mesh);
        },
        onProgress,
        () => {
          setError(true);
          setLoading(false);
        }
      );
    } else {
      // ---------- Secure STL fetch ----------
      // The mesh bytes are XOR-encrypted with a per-token key so anyone
      // watching the Network tab / saving the response only ever sees
      // opaque `application/octet-stream` garbage. Full decryption
      // happens in-memory in this component.
      const fetchSecureStl = async () => {
        try {
          const idFromProp = listingId;
          const idFromUrl = typeof url === "string" ? (url.match(/\/listings\/([^/?#]+)\/preview/) || [])[1] : null;
          const lid = idFromProp || idFromUrl;
          if (!lid) throw new Error("no-listing-id");
          const { data: tok } = await api.post(`/listings/${lid}/preview-token`);
          const key = new Uint8Array(
            tok.key_hex.match(/.{1,2}/g).map((h) => parseInt(h, 16))
          );
          // Custom XHR so we can drive the progress bar with the actual
          // download percentage on the encrypted stream.
          const buf = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("GET", `${API}/listings/${lid}/mesh?t=${encodeURIComponent(tok.token)}`, true);
            xhr.responseType = "arraybuffer";
            xhr.onprogress = (ev) => {
              if (ev.lengthComputable) setProgress(Math.min(1, ev.loaded / ev.total));
              else setProgress(-1);
            };
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response);
              else reject(new Error(`HTTP ${xhr.status}`));
            };
            xhr.onerror = () => reject(new Error("network"));
            xhr.send();
          });
          // XOR-decrypt in place using the session key.
          const bytes = new Uint8Array(buf);
          const kl = key.length;
          for (let i = 0; i < bytes.length; i++) bytes[i] ^= key[i % kl];
          const loader = new STLLoader();
          const geometry = loader.parse(bytes.buffer);
          stripFarTriangles(geometry);
          geometry.computeVertexNormals();
          geometry.center();
          const mesh = new THREE.Mesh(geometry, mkMaterial());
          placeObject(mesh);
        } catch (e) {
          console.warn("Secure mesh fetch failed:", e);
          setError(true);
          setLoading(false);
        }
      };
      fetchSecureStl();
    }

    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);
    document.addEventListener("fullscreenchange", onResize);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(slowTimer);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("fullscreenchange", onResize);
      controls.dispose();
      renderer.dispose();
      meshRef.current = null;
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, dmUrl, source]);

  useEffect(() => {
    const conf = MATERIALS[mat];
    forEachMesh(meshRef.current, (m) => {
      m.material.wireframe = wireframe;
      m.material.color.setHex(wireframe ? 0x9fd8ff : conf.color);
      m.material.metalness = wireframe ? 0.9 : conf.metalness;
      m.material.roughness = conf.roughness;
      if (m.material.emissive) m.material.emissive.setHex(0x1e40af);
      m.material.emissiveIntensity = wireframe ? 0.5 : 0.0;
      m.material.needsUpdate = true;
    });
  }, [wireframe, mat, loading]);

  useEffect(() => {
    const l = lightsRef.current;
    if (!l) return;
    const p = LIGHTING[lighting];
    l.ambient.intensity = p.ambient;
    l.key.intensity = p.key;
    l.fill.intensity = p.fill;
    l.renderer.toneMappingExposure = p.exposure;
  }, [lighting, loading]);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => setFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setFullscreen(false)).catch(() => {});
    }
  };

  // ------------------------------------------------------------------
  // Measurement tool: point-to-point distance in millimetres.
  // Snaps clicks to the mesh surface via raycasting. Multiple measurements
  // supported; each stored as an [a, b, mm] tuple in state and rendered as
  // a THIN CYLINDER MESH (THREE.Line's `linewidth` is a no-op on most
  // GPUs, so a real 3D cylinder is the only way to get a bold, readable
  // line at any camera angle). A live preview cylinder follows the cursor
  // between the first and second clicks so the seller can see where the
  // line will land before committing.
  // ------------------------------------------------------------------
  const MAX_MEASUREMENTS = 8;
  const [hoverPoint, setHoverPoint] = useState(null); // live cursor point after first click

  const raycastMeshAt = (clientX, clientY, rect) => {
    const camera = cameraRef.current;
    const mesh = meshRef.current;
    if (!camera || !mesh) return null;
    const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -((clientY - rect.top) / rect.height) * 2 + 1;
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(nx, ny), camera);
    const meshes = [];
    forEachMesh(mesh, (m) => meshes.push(m));
    const hits = raycaster.intersectObjects(meshes, true);
    return hits.length ? hits[0].point.clone() : null;
  };

  const handleMeasureClick = (evt) => {
    if (!measureMode || loading || error) return;
    const canvas = evt.currentTarget;   // the mount div, not the child bubbled from
    const rect = canvas.getBoundingClientRect();
    const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
    const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
    const point = raycastMeshAt(clientX, clientY, rect);
    if (!point) return;
    if (!pendingPoint) {
      setPendingPoint(point);
      setHoverPoint(point.clone()); // seed preview line at zero length
      return;
    }
    // Second click → finalise a measurement
    const dispDist = pendingPoint.distanceTo(point);
    const mm = dispDist * displayToMmRef.current;
    setMeasurements((prev) => {
      const next = [...prev, { a: pendingPoint, b: point, mm }];
      return next.slice(-MAX_MEASUREMENTS);
    });
    setPendingPoint(null);
    setHoverPoint(null);
  };

  // Live-preview: while the seller has clicked once but not yet twice, track
  // the pointer over the mesh so the preview cylinder stretches with them.
  const handleMeasureMove = (evt) => {
    if (!measureMode || !pendingPoint) return;
    const rect = evt.currentTarget.getBoundingClientRect();
    const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
    const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
    const p = raycastMeshAt(clientX, clientY, rect);
    if (p) setHoverPoint(p);
  };

  // Build a bold-looking cylinder between two points. Cylinder default axis
  // is +Y; we translate to the midpoint and rotate to face the target.
  const makeCylinderLine = (a, b, color = 0xfacc15, radiusScale = 1) => {
    const displayLen = a.distanceTo(b);
    // Radius sized as a fraction of the viewer's ~2-unit scene (so the line
    // is visibly bold on tiny rings AND on large bangles).
    const radius = 0.012 * radiusScale;
    const geo = new THREE.CylinderGeometry(radius, radius, Math.max(displayLen, 1e-6), 12, 1);
    const mat = new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: false });
    const cyl = new THREE.Mesh(geo, mat);
    cyl.position.copy(a).lerp(b, 0.5);
    // Align cylinder's +Y axis with (b - a)
    const dir = new THREE.Vector3().subVectors(b, a).normalize();
    const up = new THREE.Vector3(0, 1, 0);
    const quat = new THREE.Quaternion().setFromUnitVectors(up, dir);
    cyl.setRotationFromQuaternion(quat);
    cyl.renderOrder = 999;
    return cyl;
  };

  // Endpoint dot: sits on top of both line ends so the pick location is
  // unmistakable even against complex meshes.
  const makeEndpointDot = (p, color = 0xfacc15, r = 0.025) => {
    const dot = new THREE.Mesh(
      new THREE.SphereGeometry(r, 14, 14),
      new THREE.MeshBasicMaterial({ color, depthTest: false }),
    );
    dot.position.copy(p);
    dot.renderOrder = 1000;
    return dot;
  };

  // Draw / redraw measurement lines whenever anything changes
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    // Clear old
    measureLinesRef.current.forEach((l) => scene.remove(l));
    measureLinesRef.current = [];
    // Finalised measurements: bold yellow cylinder + endpoint dots
    measurements.forEach((m) => {
      const cyl = makeCylinderLine(m.a, m.b, 0xfacc15, 1);
      scene.add(cyl); measureLinesRef.current.push(cyl);
      const d1 = makeEndpointDot(m.a); const d2 = makeEndpointDot(m.b);
      scene.add(d1); scene.add(d2);
      measureLinesRef.current.push(d1, d2);
    });
    // Live preview between pendingPoint and hoverPoint (thinner + cyan)
    if (pendingPoint && hoverPoint && pendingPoint.distanceTo(hoverPoint) > 1e-4) {
      const preview = makeCylinderLine(pendingPoint, hoverPoint, 0x22d3ee, 0.65);
      scene.add(preview); measureLinesRef.current.push(preview);
    }
    // Always show a dot at the pending first-click even when no hover yet
    if (pendingPoint) {
      const anchor = makeEndpointDot(pendingPoint, 0x22d3ee, 0.028);
      scene.add(anchor); measureLinesRef.current.push(anchor);
    }
  }, [measurements, pendingPoint, hoverPoint]);

  // When measure mode toggles off, freeze the state and re-enable auto-rotate.
  useEffect(() => {
    const ctl = controlsRef.current;
    if (!ctl) return;
    ctl.autoRotate = !measureMode;
    if (!measureMode) {
      setPendingPoint(null);
      setHoverPoint(null);
    } else if (!localStorage.getItem("jx_measure_tip_seen")) {
      setShowTip(true);
      localStorage.setItem("jx_measure_tip_seen", "1");
      setTimeout(() => setShowTip(false), 6000);
    }
  }, [measureMode]);

  // Project world-space points onto screen space so we can position HTML
  // labels next to the 3D lines. Force a re-render on animation frames
  // while measurements exist OR while the user is mid-measurement.
  const [, forceRender] = useState(0);
  useEffect(() => {
    if (!measurements.length && !pendingPoint) return;
    let raf;
    const tick = () => {
      forceRender((n) => (n + 1) % 1000);
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [measurements.length, pendingPoint]);

  const projectToScreen = (v3) => {
    const cam = cameraRef.current;
    const mount = mountRef.current;
    if (!cam || !mount) return null;
    const rect = mount.getBoundingClientRect();
    const p = v3.clone().project(cam);
    return {
      x: (p.x * 0.5 + 0.5) * rect.width,
      y: (-p.y * 0.5 + 0.5) * rect.height,
      z: p.z, // for depth culling if needed
    };
  };

  const clearMeasurements = () => {
    setMeasurements([]);
    setPendingPoint(null);
    setHoverPoint(null);
  };

  const removeMeasurement = (idx) => {
    setMeasurements((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div ref={containerRef} className="relative w-full h-full rounded-md overflow-hidden border border-border mesh-grid" style={{ background: "radial-gradient(circle at 50% 42%, #1c2b4a 0%, #0b1120 70%)" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 45%, rgba(125,211,252,0.18), transparent 55%)" }} />
      <div
        ref={mountRef}
        className="absolute inset-0"
        data-testid="cad-viewer-canvas"
        onClick={handleMeasureClick}
        onMouseMove={handleMeasureMove}
        onTouchEnd={handleMeasureClick}
        onTouchMove={handleMeasureMove}
        style={{ cursor: measureMode ? "crosshair" : "grab" }}
      />

      {/* --- Measurement labels overlay --- */}
      {!loading && measurements.map((m, i) => {
        const mid = m.a.clone().lerp(m.b, 0.5);
        const p = projectToScreen(mid);
        if (!p) return null;
        return (
          <div
            key={i}
            className="absolute pointer-events-auto z-20 flex items-center gap-1 bg-amber-400 text-slate-950 font-mono text-xs font-bold px-2 py-0.5 rounded shadow-lg -translate-x-1/2 -translate-y-1/2 border-2 border-amber-600"
            style={{ left: `${p.x}px`, top: `${p.y}px` }}
            data-testid={`measurement-${i}`}
          >
            {m.mm.toFixed(1)} mm
            <button
              onClick={(e) => { e.stopPropagation(); removeMeasurement(i); }}
              className="hover:text-red-700 leading-none"
              aria-label="Remove measurement"
              data-testid={`measurement-remove-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}

      {/* --- Live-preview mm label between clicks --- */}
      {!loading && measureMode && pendingPoint && hoverPoint && (() => {
        const mid = pendingPoint.clone().lerp(hoverPoint, 0.5);
        const p = projectToScreen(mid);
        if (!p) return null;
        const mm = pendingPoint.distanceTo(hoverPoint) * displayToMmRef.current;
        return (
          <div
            className="absolute pointer-events-none z-20 bg-cyan-400/95 text-slate-950 font-mono text-xs font-bold px-2 py-0.5 rounded shadow-lg -translate-x-1/2 -translate-y-1/2 border-2 border-cyan-600"
            style={{ left: `${p.x}px`, top: `${p.y}px` }}
            data-testid="measurement-preview"
          >
            {mm.toFixed(1)} mm
          </div>
        );
      })()}

      {/* --- First-use tooltip --- */}
      {showTip && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-black/85 text-white text-xs px-3 py-2 rounded shadow-lg z-30 max-w-xs text-center" data-testid="measure-tooltip">
          Tap two points on the design to measure the distance between them in millimetres.
        </div>
      )}

      {loading && !error && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 bg-slate-950/50 backdrop-blur-sm"
          data-testid="cad-viewer-loading"
        >
          <div className="w-64 max-w-full">
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="font-mono text-[10px] uppercase tracking-widest text-primary">
                Loading {source === "3dm" ? "3DM" : "3D preview"}
              </span>
              <span
                className="font-mono text-[10px] text-slate-300 tabular-nums"
                data-testid="cad-viewer-progress-pct"
              >
                {progress < 0 ? "…" : `${Math.round(progress * 100)}%`}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              {progress < 0 ? (
                <div className="h-full w-1/3 bg-primary/80 animate-[loadingSlide_1.2s_ease-in-out_infinite]" />
              ) : (
                <div
                  className="h-full bg-primary transition-[width] duration-200 ease-out"
                  style={{ width: `${Math.max(3, progress * 100)}%` }}
                  data-testid="cad-viewer-progress-bar"
                />
              )}
            </div>
            <p
              className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-400 text-center"
              data-testid="cad-viewer-progress-msg"
            >
              {slow ? "Large model, almost there…" : "Streaming mesh from origin"}
            </p>
          </div>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 gap-2">
          <Gem className="h-8 w-8" /> Preview unavailable
        </div>
      )}

      <div className="absolute top-3 left-3 flex flex-col gap-2">
        <span className="font-mono text-[10px] tracking-widest uppercase px-2 py-1 rounded bg-black/60 text-white border border-white/10 w-fit">
          {source === "3dm" ? "Editable File · 3DM (mesh preview)" : editable ? "Editable File Available · 3DM" : "Non-Editable · STL (Print Ready)"}
        </span>
        {dmUrl && (
          <div className="flex items-center gap-1 bg-black/50 border border-white/15 rounded p-0.5 w-fit">
            <button
              data-testid="source-stl"
              onClick={() => setSource("stl")}
              className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded transition-colors ${source === "stl" ? "bg-primary text-primary-foreground" : "text-white/80 hover:text-white"}`}
            >
              STL
            </button>
            <button
              data-testid="source-3dm"
              onClick={() => setSource("3dm")}
              className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded transition-colors ${source === "3dm" ? "bg-primary text-primary-foreground" : "text-white/80 hover:text-white"}`}
            >
              3DM
            </button>
          </div>
        )}
      </div>

      <div className="absolute top-3 right-3 flex items-center gap-2">
        <div className="flex items-center gap-1 bg-black/50 border border-white/15 rounded p-0.5">
          <Sun className="h-3 w-3 text-white/70 ml-1" />
          {Object.entries(LIGHTING).map(([k, v]) => (
            <button
              key={k}
              data-testid={`lighting-${k}`}
              onClick={() => setLighting(k)}
              className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded transition-colors ${lighting === k ? "bg-primary text-primary-foreground" : "text-white/80 hover:text-white"}`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <button
          data-testid="fullscreen-btn"
          onClick={toggleFullscreen}
          title="Fullscreen"
          className="p-1.5 rounded bg-black/50 text-white border border-white/15 hover:border-primary transition-colors"
        >
          {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
      </div>

      <div className="absolute bottom-3 left-3 right-3 flex flex-wrap items-center gap-2">
        <button
          data-testid="toggle-wireframe"
          onClick={() => setWireframe((w) => !w)}
          className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded border transition-colors ${wireframe ? "bg-primary text-primary-foreground border-primary" : "bg-black/50 text-white border-white/15 hover:border-primary"}`}
        >
          <Boxes className="h-3.5 w-3.5 inline mr-1" /> Wireframe
        </button>
        <button
          data-testid="toggle-shaded"
          onClick={() => setWireframe(false)}
          className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded border transition-colors ${!wireframe ? "bg-primary text-primary-foreground border-primary" : "bg-black/50 text-white border-white/15 hover:border-primary"}`}
        >
          Shaded
        </button>
        <button
          data-testid="toggle-measure"
          onClick={() => setMeasureMode((m) => !m)}
          title="Measure distance in mm"
          className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded border transition-colors ${measureMode ? "bg-amber-400 text-slate-950 border-amber-500" : "bg-black/50 text-white border-white/15 hover:border-primary"}`}
        >
          <Ruler className="h-3.5 w-3.5 inline mr-1" /> Measure
        </button>
        {measurements.length > 0 && (
          <button
            data-testid="clear-measurements"
            onClick={clearMeasurements}
            className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded border border-white/15 bg-black/50 text-white hover:border-red-400 transition-colors"
          >
            Clear all ({measurements.length})
          </button>
        )}
        <div className="flex-1" />
        {Object.entries(MATERIALS).map(([k, v]) => (
          <button
            key={k}
            data-testid={`material-${k}`}
            onClick={() => {
              setMat(k);
              setWireframe(false);
            }}
            title={v.label}
            className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${mat === k && !wireframe ? "border-primary" : "border-white/30"}`}
            style={{ backgroundColor: "#" + v.color.toString(16).padStart(6, "0") }}
          />
        ))}
      </div>
    </div>
  );
}
